
import {
	type CalendarDate,
	getLocalTimeZone,
	isSameMonth,
	isToday,
} from "@internationalized/date";
import { useCalendarCell } from "@react-aria/calendar";
import { useFocusRing } from "@react-aria/focus";
import { mergeProps } from "@react-aria/utils";
import type { CalendarState } from "@react-stately/calendar";
import { cn } from "@workspace/web-ui/lib/utils";
import { RefObject, useRef } from "react";

export function CalendarCell({
	state,
	date,
	currentMonth,
}: {
	state: CalendarState;
	date: CalendarDate;
	currentMonth: CalendarDate;
}) {
	const ref = useRef<HTMLDivElement>(null);
	const { cellProps, buttonProps, isSelected, isDisabled, formattedDate } =
		// @ts-expect-error - RefObject is not supported in useCalendarCell
		useCalendarCell({ date }, state, ref as unknown as RefObject<Element>);

	const isOutsideMonth = !isSameMonth(currentMonth, date);

	const isDateToday = isToday(date, getLocalTimeZone());

	const { focusProps, isFocusVisible } = useFocusRing();

	return (
		<td
			{...cellProps}
			className={cn("py-0.5 relative px-0.5", isFocusVisible ? "z-10" : "z-0")}
		>
			<div
				{...mergeProps(buttonProps, focusProps)}
				ref={ref}
				hidden={isOutsideMonth}
				className="size-10 outline-none group rounded-md"
			>
				<div
					className={cn(
						"size-full rounded-md flex items-center justify-center",
						"text-sm font-semibold relative",
						isDisabled
							? isDateToday
								? "cursor-default "
								: "text-foreground/50 cursor-not-allowed"
							: "cursor-pointer",
						// Focus ring, visible while the cell has keyboard focus.
						isFocusVisible &&
							"ring-1 group-focus:z-2 ring-primary ring-offset-1",
						// Darker selection background for the start and end.
						isSelected && "bg-primary rounded-full text-primary-foreground",
						// Hover state for non-selected cells.
						!isSelected && !isDisabled && "hover:ring-2 hover:ring-primary rounded-full",
					)}
				>
					{formattedDate}
					{isDateToday && (
						<div
							className={cn(
								"absolute bottom-2 left-1/2 transform -translate-x-1/2 translate-y-1/2 size-1.5 bg-primary/80 rounded-full",
								isSelected && "bg-primary",
							)}
						/>
					)}
				</div>
			</div>
		</td>
	);
}
